'use client'

import { es } from 'date-fns/locale'
import * as React from 'react'
import { DayPicker as DayPickerRoot } from 'react-day-picker'
import { cn } from '#app/shared/lib/misc.js'
import { Icon } from '../icons/Icon.tsx'
import { buttonVariants } from './button/Button.tsx'

export type CalendarProps = React.ComponentProps<typeof DayPickerRoot> & {
  className?: string
  classNames?: Record<string, string>
  showOutsideDays?: boolean
  fromYear?: number
  toYear?: number
}

function DatePicker({
  className,
  classNames,
  showOutsideDays = true,
  fromYear,
  toYear,
  ...props
}: CalendarProps) {
  return (
    <DayPickerRoot
      locale={es}
      showOutsideDays={showOutsideDays}
      captionLayout="dropdown"
      fromYear={fromYear}
      toYear={toYear}
      className={cn('w-fit bg-white p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center space-x-2 pt-1 bg-white',
        caption_label: 'hidden',
        nav_button: cn(
          buttonVariants({ variant: 'outlineInput' }),
          `
            h-7 w-7 bg-transparent p-0 opacity-50

            hover:opacity-100
          `,
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell:
          'text-foreground-muted rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        day: 'text-center text-sm p-0 relative [&:has([aria-selected])]:bg-accent-100 first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20',
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          `
            h-9 w-9 p-0 font-normal

            aria-selected:opacity-100
          `,
        ),
        day_selected:
          'bg-primary text-white hover:bg-primary-600 hover:text-white focus:bg-foreground focus:text-white',
        day_today: 'bg-accent-100 text-accent',
        day_outside: 'text-foreground-muted opacity-50',
        disabled: 'text-foreground-muted opacity-50',
        day_range_middle:
          'aria-selected:bg-accent-100 aria-selected:text-accent',
        day_hidden: 'invisible',
        tbody: !showOutsideDays ? '[&>*:first-child]:justify-end' : '',
        ...classNames,
      }}
      modifiersClassNames={{ booked: 'bg-primary opacity-50 text-white' }}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return (
              <button {...props}>
                <Icon name="basic/chevron-left" />
              </button>
            )
          }
          return (
            <button {...props}>
              <Icon name="basic/chevron-right" />
            </button>
          )
        },
      }}
      {...props}
    />
  )
}
DatePicker.displayName = 'Calendar'

export { DatePicker }
